import { Dialog, DialogContent, DialogTitle, List, ListItem } from '@mui/material';
import { AgBarSeriesItemStylerParams, AgBarSeriesStyle, AgSparklineOptions } from 'ag-charts-community';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { isSameDay } from 'date-fns';
import Holidays from 'date-holidays';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMonthlyTempWorkersQuery } from '../../../../hooks/queries/useTempWorkersQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { useTheme } from '../../../../hooks/useTheme';
import { MonthlyTempWorker } from '../../../../services/apiSLTP/materials/tempWorkers';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { getColorByPercentage } from '../../../../utils/colors';
import { crudStatus, permissionLevels } from '../../../../utils/enums';
import StateCell from '../../../cells/StateCell';
import IconButton from '../../../shared/IconButton';
import GenericAgGridTable from '../../GenericAgGridTable';
import dayjs from 'dayjs';
const hd = new Holidays('FR');

interface MonthlyTempWorkerTypesTableProps {
    currentDate: Date;
}

const MonthlyTempWorkerTypesTable: React.FC<MonthlyTempWorkerTypesTableProps> = ({ currentDate }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { muiTheme, mode } = useTheme();
    const [selectedTempWorkerType, setSelectedTempWorkerType] = useState<MonthlyTempWorker['dailyTempWorkersByDate'][number]['daily'] | null>(null);

    const { data: dataMonthlyTempWorkerTypes, isLoading: isMonthlyTempWorkerTypesLoading } = useMonthlyTempWorkersQuery({
        params: { date: currentDate.toISOString() },
    });

    const daysInMonth = useMemo(() => {
        const days = [];
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const numDays = new Date(year, month + 1, 0).getDate();

        for (let day = 1; day <= numDays; day++) {
            const d = new Date(year, month, day);
            days.push({
                date: d,
                label: d.getDate().toString(),
            });
        }
        return days;
    }, [currentDate]);

    const rowData = useMemo(() => {
        if (!dataMonthlyTempWorkerTypes) return [];

        return dataMonthlyTempWorkerTypes.sort((a, b) => a.designation.localeCompare(b.designation));
    }, [dataMonthlyTempWorkerTypes, daysInMonth]);

    const itemStyler: (params: AgBarSeriesItemStylerParams<any>) => AgBarSeriesStyle = (params) => {
        const max = Math.max(...rowData.map((value) => value.dailyTempWorkersByDate.length));

        const color = getColorByPercentage(params.yValue ?? 0, max);
        return { fill: color };
    };

    const columnDefs: ColDef[] = useMemo(() => {
        const baseCols: ColDef<MonthlyTempWorker>[] = [
            {
                headerName: capitalizeFirstLetter(t('name')),
                field: 'designation',
                colId: 'name',
                pinned: 'left',
                initialWidth: 300,
                cellStyle: (params) =>
                    params.data?.crudStatus === crudStatus.DELETED
                        ? { color: muiTheme.palette.error.main }
                        : params.data?.crudStatus === crudStatus.INACTIVE
                          ? { color: muiTheme.palette.action.disabled }
                          : undefined,
            },
            {
                headerName: capitalizeFirstLetter(t('codeProC')),
                field: 'codeProC',
                colId: 'codeProC',
                pinned: 'left',
                initialWidth: 120,
            },

            {
                headerName: capitalizeFirstLetter(t('prodDays')),
                colId: 'totalparkline',
                cellRenderer: 'agSparklineCellRenderer',
                pinned: 'left',
                valueGetter: (params) => {
                    return [params.data?.dailyTempWorkersByDate.length];
                },
                comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
                    return valueA - valueB;
                },
                cellRendererParams: {
                    sparklineOptions: {
                        type: 'bar',
                        direction: 'horizontal',
                        min: 0,
                        max: Math.max(...rowData.map((value) => value.dailyTempWorkersByDate.length)) + 5,

                        label: {
                            enabled: true,
                            placement: 'outside-end',
                            color: mode === 'dark' ? '#fff' : '#000',
                        },

                        itemStyler: itemStyler,
                    } as AgSparklineOptions,
                },
            },
        ];

        const dateCols: ColDef<MonthlyTempWorker>[] = daysInMonth.map(({ date, label }) => ({
            headerName: label,
            colId: label,
            initialWidth: 120,
            valueGetter: (params) => params.data?.dailyTempWorkersByDate.find((value) => isSameDay(new Date(value.date), date))?.daily.length,
            cellRenderer: (params: ICellRendererParams<MonthlyTempWorker>) => {
                const currentTempWorkerTypeDay = params.data?.dailyTempWorkersByDate.find((value) => isSameDay(new Date(value.date), date));
                return currentTempWorkerTypeDay ? (
                    <div className="fx-r fx-items-c gap-5px text-grey">
                        <StateCell text={currentTempWorkerTypeDay.daily.length.toString()} stateColor={''} showDot={false} />
                        {!!currentTempWorkerTypeDay.daily.length && (
                            <IconButton iconName="infoSharp" onClick={() => setSelectedTempWorkerType(currentTempWorkerTypeDay.daily)} />
                        )}
                    </div>
                ) : (
                    <div className=" text-gray">{t('n/a').toUpperCase()}</div>
                );
            },
            headerStyle: {
                color: hd.isHoliday(date)
                    ? muiTheme.palette.error.main
                    : date.getDay() === 6
                      ? muiTheme.palette.warning.main
                      : date.getDay() === 0
                        ? muiTheme.palette.error.main
                        : '',
            },
            suppressFloatingFilterButton: true,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
        }));

        const adminCols: ColDef<MonthlyTempWorker>[] =
            user?.profileId.permsLevel === permissionLevels.ADMIN
                ? [
                      {
                          headerName: '_id',
                          field: '_id',
                          colId: '_id',
                          initialWidth: 250,
                          cellClass: 'usage-col-main',
                          headerClass: 'usage-col-main',
                      },
                  ]
                : [];

        return [...baseCols, ...dateCols, ...adminCols];
    }, [t, daysInMonth, user, rowData, mode]);

    return (
        <>
            <GenericAgGridTable
                rowData={rowData}
                columnDefs={columnDefs}
                exportCsvFileName={`presence-tempWorkers-${dayjs(currentDate).format('YYYY-MM')}`}
                agGridProps={{ getRowId: ({ data }) => data._id }}
                loading={isMonthlyTempWorkerTypesLoading}
            />
            <Dialog open={!!selectedTempWorkerType} onClose={() => setSelectedTempWorkerType(null)}>
                <DialogTitle>{capitalizeFirstLetter(t('sites'))}</DialogTitle>
                <DialogContent>
                    <List>
                        {selectedTempWorkerType?.length ? (
                            selectedTempWorkerType?.map((daily, i) => (
                                <ListItem key={i} className="gap-5px fx-r">
                                    {daily.dailyTempWorker.tempWorkerName || capitalizeFirstLetter(t('unknown'))}
                                    <span>{'-'}</span>
                                    <span className="font-w-700">{daily?.site.name}</span>
                                    <span>{'-'}</span>
                                    <span>{daily.dailyTempWorker.durationHours}</span>
                                </ListItem>
                            ))
                        ) : (
                            <ListItem>{t('noSite')}</ListItem>
                        )}
                    </List>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default MonthlyTempWorkerTypesTable;
